/* eslint-disable no-restricted-globals */
import {Dispatch, SetStateAction, useCallback, useEffect, useState} from "react";
import {Static, StaticDecode, TSchema} from "typebox";
import {Value} from "typebox/value";
import * as _ from "lodash-es";

// List of callbacks to call when session storage is changed to allow
// components to synchronize with each other
const SESSION_STORAGE_WATCHERS = new Map<string, Set<() => void>>();

// Listen to storage changes from other windows
addEventListener("storage", event => {
  if (event.storageArea === sessionStorage) {
    const watchers =
      event.key == null
        ? [...SESSION_STORAGE_WATCHERS.values()].flatMap(cbs => [...cbs])
        : [...(SESSION_STORAGE_WATCHERS.get(event.key) ?? [])];
    for (const watcher of watchers) {
      watcher();
    }
  }
});

export type TJson<T extends TSchema> = Static<T> extends unknown ? T : never;

// Helper function to deserialize a state from session storage, and
// return a default value if the state is unset or otherwise fails
// to deserialize.
export function readSessionState<S extends TSchema>(
  storageKey: string,
  schema: TJson<S>,
  defaultValue: StaticDecode<S>,
): StaticDecode<S> {
  const storedValue = sessionStorage.getItem(storageKey);
  try {
    if (storedValue) {
      return Value.Decode(schema, JSON.parse(storedValue));
    } else {
      return defaultValue;
    }
  } catch (_ex) {
    return defaultValue;
  }
}

// Helper function to serialize a state to session storage
export function writeSessionState<S extends TSchema>(storageKey: string, schema: TJson<S>, value: StaticDecode<S>) {
  // Save the new state
  const newValue = JSON.stringify(Value.Encode(schema, value));
  if (sessionStorage.getItem(storageKey) === newValue) {
    // Early exit if the value is unchanged
    return;
  }
  sessionStorage.setItem(storageKey, newValue);
  // Run any watchers to ensure all components receive the new state
  const watchers = SESSION_STORAGE_WATCHERS.get(storageKey);
  if (watchers) {
    for (const watcher of watchers) {
      watcher();
    }
  }
}

export function subscribeSessionState(storageKey: string, cb: () => void) {
  if (!SESSION_STORAGE_WATCHERS.has(storageKey)) {
    SESSION_STORAGE_WATCHERS.set(storageKey, new Set());
  }
  SESSION_STORAGE_WATCHERS.get(storageKey)!.add(cb);
  cb();

  // Return an unsubscribe function
  return () => {
    SESSION_STORAGE_WATCHERS.get(storageKey)!.delete(cb);
  };
}

// Similar to `useState`, except that state is persisted in `sessionStorage` under
// the provided `storageKey`. State must be JSON serializable.
export function useSessionState<S extends TSchema>(
  storageKey: string,
  schema: TJson<S>,
  defaultValue: StaticDecode<S>,
): [StaticDecode<S>, Dispatch<SetStateAction<StaticDecode<S>>>] {
  // On state initialization, attempt to read the value from `sessionStorage`.
  const [state, setState] = useState<StaticDecode<S>>(() => readSessionState(storageKey, schema, defaultValue));

  // Register a session storage watcher for this storage key so we get notified
  // if another component modifies the `sessionStorage`.
  useEffect(() => {
    // Define a callback which updates our state based on the session storage
    const cb = () => {
      const newState = readSessionState(storageKey, schema, defaultValue);
      setState(oldState => (_.isEqual(oldState, newState) ? oldState : newState));
    };

    return subscribeSessionState(storageKey, cb);
  }, [storageKey, schema, defaultValue, state]);

  // This is the function we return to the caller to allow them to set the state
  const wrappedSetState = useCallback(
    // Mirroring `useState`, the argument can be either a state value or a state-updating function
    (newValueOrFn: SetStateAction<StaticDecode<S>>) => {
      let newValue: StaticDecode<S>;
      // If the caller provided a function, then apply the function to the freshly-read state
      if (typeof newValueOrFn === "function") {
        newValue = (newValueOrFn as (oldValue: StaticDecode<S>) => StaticDecode<S>)(
          readSessionState(storageKey, schema, defaultValue),
        );
      } else {
        newValue = newValueOrFn;
      }

      writeSessionState(storageKey, schema, newValue);
    },
    [storageKey, schema, defaultValue],
  );

  // Finally, return the state and state setter
  return [state, wrappedSetState];
}

export function clearSessionState() {
  sessionStorage.clear();
  for (const watchers of SESSION_STORAGE_WATCHERS.values()) {
    for (const watcher of watchers) {
      watcher();
    }
  }
}
